import type { ComponentChildren } from 'preact'

interface Props {
  /** Band label, shown as a small uppercase heading above the content. */
  title: string
  /** Optional helper text under the heading. */
  description?: string
  /** Optional right-aligned controls in the heading row (e.g. a filter scoped to this band). */
  actions?: ComponentChildren
  /** Extra classes for the section wrapper. */
  className?: string
  /** Band content — typically a tile grid. */
  children: ComponentChildren
}

/**
 * A labeled band within an {@link AnalyticsPageLayout}: a small uppercase heading (and
 * optional helper text) over its content grid, with an optional right-aligned `actions`
 * slot for controls scoped to this band. Groups the dashboard's many stat tiles into
 * scannable, named clusters instead of one undifferentiated wall of numbers.
 */
export function DashboardSection({ title, description, actions, className = '', children }: Props) {
  return (
    <section className={className}>
      <div className="mb-3 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{title}</h3>
          {description && <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">{description}</p>}
        </div>
        {actions && <div className="flex items-center gap-3 flex-shrink-0">{actions}</div>}
      </div>
      {children}
    </section>
  )
}
